/**
 * Beem — Offline queue for pedidos.
 *
 * Usa IndexedDB (nativo, sin deps) para guardar pedidos armados sin internet.
 * Al volver la red, `syncAll` los reenvía uno a uno al backend.
 *
 * Cada pedido guardado lleva:
 *   - localId: id único local (uuid) usado como fake id en la UI
 *   - payload: cuerpo tal cual se enviará al POST /pedidos
 *   - snapshot: información visible del pedido (cliente, items, total) para mostrar en la UI
 *   - status: 'pending' | 'syncing' | 'error'
 *   - createdAt, lastAttemptAt, lastError
 */

const DB_NAME = "beem-offline";
const DB_VERSION = 1;
const STORE = "pedidos_pendientes";

function uuid() {
  return "off-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "localId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const result = fn(store);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function addPending({ payload, snapshot }) {
  const rec = {
    localId: uuid(),
    payload,
    snapshot,
    status: "pending",
    createdAt: new Date().toISOString(),
    lastAttemptAt: null,
    lastError: null,
  };
  await tx("readwrite", (s) => s.add(rec));
  return rec;
}

export async function listPending() {
  return new Promise(async (resolve) => {
    const db = await openDB();
    const t = db.transaction(STORE, "readonly");
    const req = t.objectStore(STORE).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

export async function countPending() {
  const items = await listPending();
  return items.length;
}

async function updatePending(localId, patch) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, "readwrite");
    const store = t.objectStore(STORE);
    const getReq = store.get(localId);
    getReq.onsuccess = () => {
      const rec = getReq.result;
      if (!rec) return resolve(false);
      const merged = { ...rec, ...patch };
      store.put(merged);
    };
    t.oncomplete = () => resolve(true);
    t.onerror = () => reject(t.error);
  });
}

export async function removePending(localId) {
  await tx("readwrite", (s) => s.delete(localId));
}

export async function clearAll() {
  await tx("readwrite", (s) => s.clear());
}

let syncing = false;
const listeners = new Set();
export function onQueueChange(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function notify() { listeners.forEach((f) => { try { f(); } catch (e) { console.warn("offlineQueue listener error", e); } }); }

/**
 * Sync all pending orders. Uses the provided axios `api` instance.
 * Returns { ok, failed, remaining }.
 */
export async function syncAll(api) {
  if (syncing || !navigator.onLine) return { ok: 0, failed: 0, remaining: await countPending() };
  syncing = true;
  let ok = 0, failed = 0;
  const items = await listPending();
  for (const rec of items) {
    try {
      await updatePending(rec.localId, { status: "syncing", lastAttemptAt: new Date().toISOString() });
      notify();
      await api.post("/pedidos", rec.payload);
      await removePending(rec.localId);
      ok++;
    } catch (err) {
      let msg = err?.response?.data?.detail || err?.message || "Error";
      if (typeof msg !== "string") {
        try { msg = JSON.stringify(msg); } catch { msg = "Error de validación"; }
      }
      await updatePending(rec.localId, { status: "error", lastError: msg });
      failed++;
    }
    notify();
  }
  syncing = false;
  const remaining = await countPending();
  return { ok, failed, remaining };
}

/**
 * Should we queue this order? True when offline OR when explicitly forced.
 */
export function shouldQueue() {
  return typeof navigator !== "undefined" && !navigator.onLine;
}

export function buildLocalPedido({ payload, snapshot, localId }) {
  return {
    id: localId,
    __offline: true,
    numero: `OFFLINE-${localId.slice(-5).toUpperCase()}`,
    estado: "borrador",
    ...snapshot,
    created_at: new Date().toISOString(),
  };
}
